import type { FC } from "react";

import { Button } from "@/components/ui/button";

import { pendingIsMissing, sourceLabel } from "../model";
import type { KeyFact, KeyFactActionBody } from "../types";

/**
 * A changed value's question: take the recomputed value or keep the current
 * one. When the record no longer gives a value, keeping it is the default
 * and clearing it the other answer.
 */
export const FactDecision: FC<{
  fact: KeyFact;
  busy: boolean;
  onRespond: (body: KeyFactActionBody) => void;
  onSkip: () => void;
}> = ({ fact, busy, onRespond, onSkip }) => {
  const { pending } = fact;
  if (!pending) return null;
  const missing = pendingIsMissing(pending);
  const from = (pending.sources ?? []).map(sourceLabel).join(", ");
  return (
    <div className="flex flex-col gap-2.5" data-testid="fact-decision">
      <div className="flex flex-col gap-0.5">
        <p className="text-base font-medium text-fg">
          {missing ? (
            "The record no longer supports this value"
          ) : (
            <>
              The record now says {pending.display}
              {fact.display ? (
                <span className="font-normal text-fg-3">
                  {" "}
                  (was {fact.display})
                </span>
              ) : null}
            </>
          )}
        </p>
        {pending.rationale || from ? (
          <p className="text-sm whitespace-pre-line text-fg-3">
            {pending.rationale || `From ${from}.`}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {missing ? (
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => onRespond({ action: "dismiss" })}
            >
              Keep {fact.display ? "the Current Value" : "It"}
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onRespond({ action: "confirm" })}
            >
              Clear the Value
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => onRespond({ action: "confirm" })}
            >
              Use New Value
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onRespond({ action: "dismiss" })}
            >
              Keep
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onSkip}>
          Skip
        </Button>
      </div>
    </div>
  );
};
